import type { ProcessoTriagem } from '@/features/secretaria-arbitral/types/triagem';

/**
 * Resultado de pedir a abertura de um caso no painel de triagem.
 *
 * `foraDaJanela` significa "não está na PÁGINA carregada" — o painel pagina sob
 * demanda (`FILA_TRIAGEM_LIMIT` por vez) e, com a fila ordenada por
 * `dataEntrada` ASC, o processo recém-criado é justamente o que não está nela.
 * Não é um beco sem saída: quem recebe este marcador vai buscar o caso no
 * servidor por id (`fetchCasoTriagemAlvo`), e só o que o servidor não conhece
 * termina em "não foi possível abrir".
 *
 * Antes, `selecionar` fazia `if (!alvo) return` — falhava em silêncio, e o
 * painel exibia "Não há processos aguardando triagem" sobre um processo que
 * existe e espera decisão (web#1063).
 */
export interface SelecaoTriagem {
  /** Sempre preenchido: o caso pedido continua sendo o alvo, esteja ou não carregado. */
  selecionadoId: string;
  /** `id` quando o caso NÃO está na janela carregada; `null` quando está. */
  foraDaJanela: string | null;
}

/**
 * Compara um identificador vindo da URL com um devolvido pelo servidor.
 *
 * Sem caixa: `ehUuid` aceita maiúsculas (a régua do core também), então um
 * deep-link com o UUID em maiúsculas ROTEIA como id — e a comparação exata
 * falharia contra o id minúsculo do banco, dizendo "não foi possível abrir" para
 * um caso que existe. Seria o defeito do web#1063 de novo, reintroduzido pela
 * própria reconferência que existe para evitá-lo.
 *
 * Vale para as três comparações do caminho do caso pedido — a do thunk, a de
 * `pedirCaso` e a do efeito que pousa a seleção. Consertar só uma deixaria o
 * caso ser encontrado e nunca selecionado.
 */
export function mesmoIdentificador(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  if (!a || !b) return false;
  return a.toLowerCase() === b.toLowerCase();
}

export function resolverSelecaoTriagem(
  itens: ReadonlyArray<Pick<ProcessoTriagem, 'id'>>,
  id: string,
): SelecaoTriagem {
  const encontrado = itens.some((p) => p.id === id);
  return { selecionadoId: id, foraDaJanela: encontrado ? null : id };
}

/**
 * Qual estado vazio o painel deve mostrar. Os dois casos abaixo deixam
 * `selecionado` nulo, mas dizem coisas OPOSTAS ao usuário — colapsá-los num
 * texto só foi o que tornou o defeito indiagnosticável pela tela.
 */
export type EstadoVazioTriagem = 'FILA_VAZIA' | 'CASO_FORA_DA_JANELA';

export function estadoVazioTriagem(casoForaDaJanela: string | null): EstadoVazioTriagem {
  return casoForaDaJanela ? 'CASO_FORA_DA_JANELA' : 'FILA_VAZIA';
}

/**
 * Trocar de caso é sempre transição TOTAL: `dec`, `confirmado`, `abaAtiva` e
 * `activeDocId` descrevem o caso selecionado, e devem ser reinicializados mesmo
 * quando o alvo está fora da janela carregada.
 *
 * Existe como função para o invariante ficar testável: na primeira versão do
 * #1064 os resets ficaram DEPOIS de um `return`, e o caso novo herdava o
 * rascunho de decisão do anterior — `confirmar()` gravaria o motivo de um
 * processo contra o id de outro (COR-02 da review).
 */
export function precisaResetarEstadoDoCaso(
  selecionadoIdAnterior: string | null,
  novoId: string,
): boolean {
  return selecionadoIdAnterior !== novoId;
}
